// import React, { useMemo } from "react";
// import { useTaxData } from "./TaxrulesandTaxgroups";
// import { TaxGroup, TaxRule } from "./Manualtaxandtaxrules";
// import TooltipBubble from "../TooltipBubble";

// type Props = {
//   token: string;

//   /** Whether Taxable is ON. If false, the select is hidden and values cleared by parent. */
//   taxable: boolean;

//   /** FORMIK MODE (your current screen): */
//   formik: any;
//   taxRuleIdName?: string;   // default: "tax_rule_id"
//   groupFieldName?: string;  // default: "tax_group"
//   ruleFieldName?: string;   // default: "tax_rule"

//   /** UI tweaks */
//   wrapperClassName?: string; // applied to the inline container
//   selectClassName?: string;  // default: "form-select"
//   label?: string;            // default: "Tax Rule"
// };
// const tooltipText = "Manual tax rates are ignored while a live tax integration is in use.";


// export default function TaxRuleSelect({
//   token,
//   taxable,
//   formik,
//   taxRuleIdName = "tax_rule_id",
//   groupFieldName = "tax_group",
//   ruleFieldName = "tax_rule",
//   wrapperClassName = "d-flex flex-column ms-3",
//   selectClassName = "form-select",
//   label = "Tax Rule",
// }: Props) {
//   const { taxGroups, taxRules } = useTaxData(token);

//   // Value source: use stored id if present; else derive from existing group/rule objects (edit mode)
//   const derivedValue = useMemo(() => {
//     const v = formik.values?.[taxRuleIdName];
//     if (v) return v;

//     const g: TaxGroup | null = formik.values?.[groupFieldName] ?? null;
//     if (g?.id) return `group-${g.id}`;

//     const r: TaxRule | null = formik.values?.[ruleFieldName] ?? null;
//     if (r?._id) return `rule-${r._id}`;

//     return "";
//   }, [formik.values, taxRuleIdName, groupFieldName, ruleFieldName]);

//   const onChange = (value: string) => {
//     formik.setFieldValue(taxRuleIdName, value);

//     if (value.startsWith("group-")) {
//       const id = value.slice(6);
//       const g = taxGroups.find(x => x.id === id) || null;
//       formik.setFieldValue(groupFieldName, g);
//       formik.setFieldValue(ruleFieldName, null);
//     } else if (value.startsWith("rule-")) {
//       const id = value.slice(5);
//       const r = taxRules.find(x => x._id === id) || null;
//       formik.setFieldValue(ruleFieldName, r);
//       formik.setFieldValue(groupFieldName, null);
//     } else {
//       formik.setFieldValue(groupFieldName, null);
//       formik.setFieldValue(ruleFieldName, null);
//     }
//   };

//   if (!taxable) return null; // keep row tight; no disabled styling

//   return (
//     <div className={wrapperClassName}>
//      <label className="form-label mb-1 d-flex align-items-center gap-2">
//   {label}
//   <TooltipBubble message={tooltipText}>
//     <i className="bi bi-info-circle" />
//   </TooltipBubble>
// </label>
//       <select
//         className={selectClassName}
//         value={derivedValue}
//         onChange={(e) => onChange(e.target.value)}
//         onBlur={formik.handleBlur}
//         name={taxRuleIdName}
//       >
//         <option value="">- Select -</option>
//         {taxGroups.map((g) => (
//           <option key={`group-${g.id}`} value={`group-${g.id}`} style={{ fontWeight: "bold" }}>
//             {g.name}
//           </option>
//         ))}
//         {taxRules.map((r) => (
//           <option key={`rule-${r._id}`} value={`rule-${r._id}`}>
//             {`${r.country} - ${r.taxRate}%`}
//           </option>
//         ))}
//       </select>
//     </div>
//   );
// }


// components/services/taxrulesandTaxgroupsui.tsx
import React, { useMemo } from "react";
import { useTaxData } from "./TaxrulesandTaxgroups";
import TooltipBubble from "../TooltipBubble";

type Props = {
  token: string;
  taxable: boolean;

  formik: any;
  taxRuleIdName?: string;   // "group-<id>" | "rule-<id>" | ""
  groupFieldName?: string;  // e.g., "tax_group" (object)
  ruleFieldName?: string;   // e.g., "tax_rule"  (object)

  wrapperClassName?: string;
  selectClassName?: string;
  label?: string;

  /** NEW: view mode / disable editing */
  disabled?: boolean;
  mode?: "add" | "edit" | "view";
};

const tooltipText = "Manual tax rates are ignored while a live tax integration is in use.";

// helper: unwrap Mongo {$oid: "..."} or return primitive
const oid = (v: any) => (v && typeof v === "object" && "$oid" in v ? v.$oid : v);

export default function TaxRuleSelect({
  token,
  taxable,
  formik,
  taxRuleIdName = "tax_rule_id",
  groupFieldName = "tax_group",
  ruleFieldName = "tax_rule",
  wrapperClassName = "d-flex flex-column ms-3",
  selectClassName = "form-select",
  label = "Tax Rule",
  disabled = false,
  mode,
}: Props) {
  const isView = disabled || mode === "view";
  const { taxGroups, taxRules } = useTaxData(token);

  // normalize ids across shapes (id/_id/{$oid})
  const getGroupId = (g: any | null | undefined) => oid(g?.id ?? g?._id ?? null);
  const getRuleId  = (r: any | null | undefined) => oid(r?._id ?? r?.id ?? null);

  // Build stable encoded value for the <select>
  const derivedValue: string = useMemo(() => {
    const v = formik.values?.[taxRuleIdName];
    if (typeof v === "string" && (v.startsWith("group-") || v.startsWith("rule-"))) return v;

    // plain ids (could be strings or {$oid})
    const plainGroupId = oid(formik.values?.tax_group_id ?? null);
    const plainRuleId  = oid(formik.values?.tax_rule_id ?? null);
    if (plainGroupId) return `group-${plainGroupId}`;
    if (plainRuleId)  return `rule-${plainRuleId}`;

    // objects
    const gId = getGroupId(formik.values?.[groupFieldName] ?? null);
    const rId = getRuleId(formik.values?.[ruleFieldName] ?? null);
    if (gId) return `group-${gId}`;
    if (rId) return `rule-${rId}`;

    return "";
  }, [formik.values, taxRuleIdName, groupFieldName, ruleFieldName]);

  // Human label for a temporary option when current value isn't in lists yet
  const currentLabel = useMemo(() => {
    if (!derivedValue) return "";
    if (derivedValue.startsWith("group-")) {
      const id = derivedValue.slice(6);
      const g =
        taxGroups.find(x => oid((x as any).id ?? (x as any)._id) === id) ||
        formik.values?.[groupFieldName] || null;
      return g?.name ?? `Group (${id})`;
    }
    if (derivedValue.startsWith("rule-")) {
      const id = derivedValue.slice(5);
      const r =
        taxRules.find(x => oid((x as any)._id ?? (x as any).id) === id) ||
        formik.values?.[ruleFieldName] || null;
      const country = r?.country ?? "Rule";
      const rate = r?.taxRate ?? r?.rate;
      return rate != null ? `${country} - ${rate}%` : `${country} (${id})`;
    }
    return "";
  }, [derivedValue, taxGroups, taxRules, formik.values, groupFieldName, ruleFieldName]);

  const onChange = (value: string) => {
    if (isView) return; // view-only
    formik.setFieldValue(taxRuleIdName, value);

    if (value.startsWith("group-")) {
      const id = value.slice(6);
      const g =
        taxGroups.find(x => oid((x as any).id ?? (x as any)._id) === id) ||
        { _id: id };
      formik.setFieldValue(groupFieldName, g);
      formik.setFieldValue(ruleFieldName, null);
    } else if (value.startsWith("rule-")) {
      const id = value.slice(5);
      const r =
        taxRules.find(x => oid((x as any)._id ?? (x as any).id) === id) ||
        { _id: id };
      formik.setFieldValue(ruleFieldName, r);
      formik.setFieldValue(groupFieldName, null);
    } else {
      formik.setFieldValue(groupFieldName, null);
      formik.setFieldValue(ruleFieldName, null);
    }
  };

  if (!taxable) return null;

  return (
    <div className={wrapperClassName}>
      <label className="form-label mb-1 d-flex align-items-center gap-2">
        {label}
        <TooltipBubble 
          message={tooltipText}
          position="left"
          fontSize={14}
          backgroundColor="#374151"
        >
          <i className="bi bi-info-circle" />
        </TooltipBubble>
      </label>

      <select
        className={selectClassName}
        value={derivedValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={formik.handleBlur}
        name={taxRuleIdName}
        disabled={isView}
      >
        <option value="">- Select -</option>

        {/* keep a temp option so the chosen item shows immediately */}
        {derivedValue &&
          !taxGroups.some(g => `group-${oid((g as any).id ?? (g as any)._id)}` === derivedValue) &&
          !taxRules.some(r => `rule-${oid((r as any)._id ?? (r as any).id)}` === derivedValue) && (
            <option value={derivedValue}>{currentLabel}</option>
          )}

        {/* Groups (bold) */}
        {taxGroups.map((g) => {
          const gid = oid((g as any).id ?? (g as any)._id);
          return (
            <option key={`group-${gid}`} value={`group-${gid}`} style={{ fontWeight: "bold" }}>
              {(g as any).name}
            </option>
          );
        })}

        {/* Rules */}
        {taxRules.map((r) => {
          const rid = oid((r as any)._id ?? (r as any).id);
          const country = (r as any).country ?? "Rule";
          const rate = (r as any).taxRate ?? (r as any).rate;
          const lbl = rate != null ? `${country} - ${rate}%` : country;
          return (
            <option key={`rule-${rid}`} value={`rule-${rid}`}>
              {lbl}
            </option>
          );
        })}
      </select>
    </div>
  );
}
